import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useAppScope } from "./AppCover";
import { CloseIcon } from "./icons";

/*
  Figma "Modal · მოდალი". From 600px up it is the centred Dialog (480 wide,
  radius/lg, 1px separator edge, 24 padding); on phones it is the Sheet that
  rises from the bottom (radius/xl top corners, grabber, 24 / 32 padding).
  Header: Title 3, an optional Subheadline description in label/secondary and
  a 32px round close button. Esc, the scrim and the close button all close it.
  `media` sits left of the title, `width` replaces the 480 dialog width, and
  `bare` drops the header for screens that lay out their own, like a success step, and
  `panelClass` lets them adjust the panel.
*/
const Modal = ({
  open,
  onClose,
  title,
  description,
  closeLabel = "Close",
  isDark,
  media = null,
  width = "min-[600px]:w-[480px]",
  bare = false,
  panelClass = "",
  children,
}) => {
  const titleId = useId();
  const panelRef = useRef(null);
  // Portaled out of the page, so the app's colour has to be carried over.
  const app = useAppScope();
  // Callers pass a new onClose each render; only opening and closing may move focus.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeRef.current?.();
    };
    document.addEventListener("keydown", onKeyDown);
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      data-theme={isDark === undefined ? undefined : isDark ? "dark" : "light"}
      data-app={app || undefined}
      className="fixed inset-0 z-[600] flex items-end justify-center font-ds text-label-primary min-[600px]:items-center min-[600px]:p-space-24"
    >
      <div className="absolute inset-0 bg-overlay-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={bare ? undefined : titleId}
        aria-label={bare ? title : undefined}
        tabIndex={-1}
        className={`relative flex max-h-[92vh] w-full flex-col gap-space-20 overflow-y-auto rounded-t-radius-xl bg-bg-raised px-space-24 pb-space-32 pt-space-8 shadow-overlay outline-none min-[600px]:max-h-full ${width} min-[600px]:rounded-radius-lg min-[600px]:border min-[600px]:border-separator-default min-[600px]:p-space-24 ${panelClass}`}
      >
        <div className="flex justify-center py-space-4 min-[600px]:hidden" aria-hidden="true">
          <span className="h-[5px] w-[36px] rounded-full bg-fill-secondary" />
        </div>
        {!bare && (
          <div className="flex items-start gap-space-12">
            {media}
            <div className="flex min-w-0 flex-1 flex-col gap-space-8">
              <h2 id={titleId} className="text-title-3">
                {title}
              </h2>
              {description && <p className="text-subheadline text-label-secondary">{description}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              title={closeLabel}
              className="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-fill-secondary text-label-primary outline-none before:absolute before:-inset-1.5 after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:bg-current after:opacity-0 after:transition-opacity hover:after:opacity-[var(--state-hover)] active:after:opacity-[var(--state-pressed)] focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]"
            >
              <CloseIcon />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
